import { NgModule, inject, provideAppInitializer } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { providePrimeNG } from "primeng/config";
import Aura from "@primeng/themes/aura";

import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { AppRoutingModule } from "./app-routing.module";
import { FormsModule } from "@angular/forms";

// Third Party
import { OAuthModule } from "angular-oauth2-oidc";
import { AuthConfig } from "angular-oauth2-oidc";

import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsStoragePluginModule } from "@ngxs/storage-plugin";
import { NgxsModule, NGXS_PLUGINS } from "@ngxs/store";

import { logoutPlugin } from "./ngxsLogoutPlugin";

import { FontAwesomeModule, FaIconLibrary } from "@fortawesome/angular-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";

import { BreadcrumbModule } from "xng-breadcrumb";

import { ButtonModule } from "primeng/button";
import { PaginatorModule } from "primeng/paginator";
import { TableModule } from "primeng/table";
import { AvatarModule } from "primeng/avatar";
import { AvatarGroupModule } from "primeng/avatargroup";
import { InputTextModule } from "primeng/inputtext";
import { PickListModule } from "primeng/picklist";
import { TextareaModule } from "primeng/textarea";
import { DialogModule } from "primeng/dialog";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastModule } from "primeng/toast";
import { PanelModule } from "primeng/panel";
import { MessagesModule } from "primeng/messages";
import { MessageModule } from "primeng/message";
import { SkeletonModule } from "primeng/skeleton";
import { ToggleButtonModule } from "primeng/togglebutton";
import { TreeModule } from "primeng/tree";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { StepperModule } from "primeng/stepper";
import { DatePickerModule } from "primeng/datepicker";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { MultiSelectModule } from "primeng/multiselect";
import { AccordionModule } from "primeng/accordion";

import { GridsterModule } from "angular-gridster2";

//
import { oauthConfig } from "./oauth.config";
import { AppComponent } from "./app.component";
import { DashboardComponent } from "./dashboard/dashboard.component";
import { PageNotFoundComponent } from "./page-not-found/page-not-found.component";
import { SharedModule } from "./shared/shared.module";
import { authAppInitializerFactory } from "./auth-app-initializer.factory";
import { AuthService } from "./shared/services/auth/auth.service";
import { environment } from "@env/environment";

// States
import { LocationState } from "./shared/state/location/location.state";
import { LocationsEventsState } from "./shared/state/locations_events/locations_events.state";
import { LocationEventsState } from "./shared/state/location_events/location_events.state";
import { LocationDevicesState } from "./shared/state/location_devices/location_devices.state";
import { LocationDevicesGraphsState } from "./shared/state/location_devices_graphs/location_devices_graphs.state";

import { DeviceEventsState } from "./shared/state/device_events/device_events.state";
import { AutomationState } from "./shared/state/automation/automation.state";
import { UserState } from "./shared/state/user/user.state";
import { OrganizationState } from "./shared/state/organization/organization.state";

import { OrganizationUsersState } from "./shared/state/organization_users/organization_users.state";

import { ProfileSettingsComponent } from "./profile-settings/profile-settings.component";
import { ApiErrorState } from "./shared/state/api_error/api_error.state";
import { ServiceWorkerModule } from "@angular/service-worker";
import { NewDashboardComponent } from "./new-dashboard/new-dashboard.component";
import { NewSettingsComponent } from "./new-settings/new-settings.component";
import { WelcomeComponent } from "./welcome/welcome.component";
import { SetupComponent } from "./setup/setup.component";
import { PropertyState } from "./shared/state/property/property.state";
import { IntegrationState } from "./shared/state/integrations/integration.state";
import { UiState } from "./shared/state/ui/ui.state";
import { PermissionsState } from "./shared/state/permissions/permissions.state";

import AeoCloudPreset from "./aeocloudpreset";

@NgModule({
	declarations: [
		AppComponent,
		DashboardComponent,
		PageNotFoundComponent,
		ProfileSettingsComponent,
		NewDashboardComponent,
		NewSettingsComponent,
		WelcomeComponent,
		SetupComponent,
	],
	bootstrap: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		FormsModule,
		ReactiveFormsModule,
		ButtonModule,
		PaginatorModule,
		TableModule,
		InputTextModule,
		AvatarModule,
		AvatarGroupModule,
		PickListModule,
		TextareaModule,
		DialogModule,
		ConfirmDialogModule,
		ToastModule,
		PanelModule,
		MessagesModule,
		SkeletonModule,
		ToggleButtonModule,
		ToggleSwitchModule,
		MultiSelectModule,
		AccordionModule,
		SharedModule,
		GridsterModule,
		TreeModule,
		IconFieldModule,
		InputIconModule,
		StepperModule,
		DatePickerModule,
		MessageModule,
		OAuthModule.forRoot({
			resourceServer: {
				allowedUrls: [environment.api_base_url],
				sendAccessToken: true,
			},
		}),
		FontAwesomeModule,
		BreadcrumbModule,
		environment.production ? [] : NgxsReduxDevtoolsPluginModule.forRoot(),
		NgxsModule.forRoot([
			LocationState,
			LocationsEventsState,
			LocationEventsState,
			OrganizationState,
			LocationDevicesState,
			DeviceEventsState,
			LocationDevicesGraphsState,
			AutomationState,
			UserState,
			ApiErrorState,
			OrganizationUsersState,
			PropertyState,
			IntegrationState,
			UiState,
			PermissionsState,
		]),
		NgxsStoragePluginModule.forRoot({
			key: [
				LocationState,
				LocationsEventsState,
				LocationEventsState,
				OrganizationState,
				LocationDevicesState,
				DeviceEventsState,
				LocationDevicesGraphsState,
				AutomationState,
				UserState,
				ApiErrorState,
				OrganizationUsersState,
				PropertyState,
				IntegrationState,
				UiState,
				PermissionsState,
			],
		}),
		ServiceWorkerModule.register("ngsw-worker.js", {
			enabled: environment.production,
			// Register the ServiceWorker as soon as the application is stable
			// or after 30 seconds (whichever comes first).
			registrationStrategy: "registerWhenStable:30000",
		}),
	],
	providers: [
		{ provide: AuthConfig, useValue: oauthConfig },
		provideAppInitializer(() => {
			const initializerFn = authAppInitializerFactory(inject(AuthService));
			return initializerFn();
		}),
		{
			provide: NGXS_PLUGINS,
			useValue: logoutPlugin,
			multi: true,
		},
		provideHttpClient(withInterceptorsFromDi()),
		providePrimeNG({
			theme: {
				preset: AeoCloudPreset,
				options: {
					darkModeSelector: false || "none",
				},
			},
		}),
	],
})
export class AppModule {
	constructor(library: FaIconLibrary) {
		library.addIconPacks(fas, far, fab);
	}
}
